/**
 * THE GRANNY FLAT'S GUARANTEED INCOME, PLAN BY PLAN (owner, 21 Oct 2026 — the guaranteed income review and the
 * agreed mock-up, docs/ux-review/annuities/, screen 6: granny flat works "as the village").
 *
 * One reading of a plan's guaranteed income, used by the engine cards, the hand-built plan, "Explain this" and
 * the advice document, so they cannot disagree:
 *   - who it is for (the owner's first name; a joint one names both), how much, and which type it is
 *     (familyPanelKindOf: full amount back, money back, income only);
 *   - about how much it pays a year (the projection's own first-year figure);
 *   - the plan to compare it with: the same sale, with the money invested instead (`gf-sell-invest`);
 *   - for the document, the best plan tested with no guaranteed income at all.
 *
 * Only structural types here, so the page and gfSoa.ts can both import it without a cycle.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { familyPanelKindOf, type FamilyPanelKind } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { deploymentAnnuityTerms, deploymentIsIncomeOnly, type ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';

/** The comparison the family panel and the document name. */
export const GF_COMPARE_NOTE = 'the same sale, with the money invested instead';

/** The sibling plan with the same sale and no guaranteed income. */
export const GF_COMPARE_ID = 'gf-sell-invest';

interface PlanLike {
    id?: string | null;
    name?: string | null;
    inputs?: { deployment?: unknown } | Record<string, unknown> | null;
    projections?: readonly unknown[] | null;
    snapshot?: { pension?: number; netCashflow?: number; legacyValue?: number } | null;
}

export interface GfGuaranteedIncome {
    amount: number;
    kind: FamilyPanelKind;
    annuityType: 'lifetime' | 'fixedTerm' | 'guaranteed';
    residualPercent: number;
    owner: 'client' | 'partner' | 'joint';
    personName: string;
    /** Lifetime, nothing returned: the client's confirmation is needed before it can be chosen. */
    incomeOnly: boolean;
    /** The yearly income in the first year, from the projection (or the quote on the deployment). */
    incomeAYear: number | null;
    /** True when the income is a provider's quote rather than our estimate. */
    quoted: boolean;
}

const num = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
};

const deploymentOf = (plan: PlanLike | null | undefined): ProceedsDeploymentValue | undefined =>
    ((plan?.inputs as { deployment?: unknown } | null | undefined)?.deployment as ProceedsDeploymentValue | undefined) || undefined;

/** The owner as the screens name them: a first name, or both for a joint one. */
export function gfAnnuityPersonName(factFind: DraftFactFind | null | undefined, owner: 'client' | 'partner' | 'joint'): string {
    const client = factFind?.client?.firstName?.trim() || '';
    const partner = factFind?.partner?.firstName?.trim() || '';
    if (owner === 'joint' && client && partner) return `${client} and ${partner}`;
    if (owner === 'partner') return partner || 'the partner';
    return client || 'the client';
}

/** The new guaranteed income's yearly payment in the first projected year (those already on the file excluded). */
export function gfAnnuityIncomeAYear(rows: readonly unknown[] | null | undefined, factFind: DraftFactFind | null | undefined): number | null {
    const first = (rows || [])[0] as { assets?: { annuities?: Array<Record<string, unknown>> } } | undefined;
    const list = first?.assets?.annuities || [];
    const onFile = new Set((((factFind?.assets as { annuities?: Array<{ id?: unknown }> } | undefined)?.annuities) || []).map((a) => String(a?.id)));
    const total = list.filter((a) => !onFile.has(String(a?.id))).reduce((t, a) => t + num(a.annualPayment ?? a.income), 0);
    return total > 0 ? Math.round(total) : null;
}

/** The plan's guaranteed income, or null when it has none. */
export function gfGuaranteedIncomeOf(plan: PlanLike | null | undefined, factFind: DraftFactFind | null | undefined): GfGuaranteedIncome | null {
    const deployment = deploymentOf(plan);
    const terms = deploymentAnnuityTerms(deployment, factFind?.adviceFor === 'couple');
    if (!terms) return null;
    const amount = num(terms.purchaseAmount);
    if (!(amount > 0)) return null;
    const owner = (terms.owner as 'client' | 'partner' | 'joint') || 'client';
    const annuityType = (terms.annuityType as 'lifetime' | 'fixedTerm' | 'guaranteed') || 'lifetime';
    const residualPercent = num(terms.residualPercent);
    const override = num(deployment?.annuityIncomeOverride);
    return {
        amount,
        kind: familyPanelKindOf({ annuityType, annuityResidualPercent: residualPercent }),
        annuityType,
        residualPercent,
        owner,
        personName: gfAnnuityPersonName(factFind, owner),
        incomeOnly: deploymentIsIncomeOnly(deployment),
        incomeAYear: gfAnnuityIncomeAYear(plan?.projections, factFind) ?? (override > 0 ? Math.round(override) : null),
        quoted: override > 0,
    };
}

/**
 * The same sale with the money invested instead — the sibling plan the family panel compares with. A plan
 * that also clears the debts is compared with the invested plan that clears them too, where there is one.
 */
export function gfComparePlanFor<T extends PlanLike>(plan: PlanLike | null | undefined, all: readonly T[] | null | undefined): T | null {
    const id = String(plan?.id || '');
    if (!id || id === GF_COMPARE_ID) return null;
    const list = all || [];
    const debt = /-clear-debt$/.test(id) ? '-clear-debt' : '';
    return list.find((s) => String(s.id) === `${GF_COMPARE_ID}${debt}`) || list.find((s) => String(s.id) === GF_COMPARE_ID) || null;
}

/** The best plan (most left to the family) tested with no guaranteed income; never the stay-put baseline. */
export function gfBestWithoutGuaranteedIncome<T extends PlanLike>(all: readonly T[] | null | undefined, excludeId?: string | null): T | null {
    let best: T | null = null;
    for (const s of all || []) {
        const id = String(s.id || '');
        if (!id || id === 'gf-stay-put' || id === excludeId) continue;
        if (num(deploymentOf(s)?.annuity) > 0) continue;
        const estate = s.snapshot?.legacyValue;
        if (estate == null || !Number.isFinite(Number(estate))) continue;
        if (!best || Number(estate) > Number(best.snapshot?.legacyValue)) best = s;
    }
    return best;
}
